import { LEAD_GENERATED_BY, leadGeneratedByDefault } from '../shared/leadAttribution';
import { recordOutreach } from './outreach';
import { syncPipelineContactApp } from './pipelineContactSync';
import { ref, push, set, update, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import { newOwnership, indexOps, ownerOf } from './recordAccess';
import { createNotification } from './notificationService';
import { syncPocketListing, removePocketListing } from './pocketListingService';
import { syncConfirmTask } from './apptConfirm';
import { requestCalendarSync } from './googleSync';

// Pocket listing mirror is best-effort: a failure there never blocks saving the seller interest.
const mirror = async (interest, uid) => { try { await syncPocketListing(interest, uid); } catch (e) { console.error('Could not update pocket listing', e); } };

// Seller Interests (in-app only, signed-in users). Stored under crm/sellerInterests/{id},
// which the existing crm/ rules already cover, so no new database rule is needed.
// Nothing here sends an email or text: alerts are in-app notifications and tasks only.

export const SI_PATH = 'crm/sellerInterests';
export const SI_STATUSES = ['New', 'Contacted', 'Appointment set', 'Listing signed', 'Not now', 'Converted'];
export const SI_RESULTS = ['Wants to list', 'Thinking about it', 'Keep in mind for off-market buyers', 'Follow back on date'];
export const OFF_MARKET_RESULT = 'Keep in mind for off-market buyers';
export const FOLLOW_BACK_RESULT = 'Follow back on date';
export const fullName = (i) => [i?.firstName, i?.lastName].filter(Boolean).join(' ').trim() || i?.name || '';
export const SI_TIMELINES = ['Now', '3 months', '6 months', 'Just curious'];
// Pipeline status (new field, separate from the old `status` values, which are left as stored).
export const SI_PIPELINE_STATUSES = ['Waiting to list', 'Thinking about it'];
export const SI_WAITING = 'Waiting to list'; // optional planned list date
// Check-in task date: two weeks before the planned date, or the date itself if that is sooner than two weeks out.
export const checkInDay = (dateStr) => {
  const d = new Date(`${dateStr}T09:00:00`); const two = new Date(d.getTime() - 14 * 864e5); const today = new Date(); today.setHours(0, 0, 0, 0);
  const pick = two >= today ? two : d;
  return `${pick.getFullYear()}-${String(pick.getMonth() + 1).padStart(2, '0')}-${String(pick.getDate()).padStart(2, '0')}`;
};


// PLACEHOLDER consent wording, taken from the Resi privacy policy (Text messages / Email sections).
// Replace with the exact language used on the Meta lead forms / SMS terms before go-live.
export const CONSENT_TEXT_PLACEHOLDER =
  'I agree to receive text messages from RESIDENCE | eXp Realty about my home. Message and data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help.';
export const CONSENT_EMAIL_PLACEHOLDER =
  'I agree to receive emails from RESIDENCE | eXp Realty about my home. I can unsubscribe at any time.';

const nowIso = () => new Date().toISOString();
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

export const EMPTY_INTEREST = {
  firstName: '', lastName: '', additionalContacts: [], phone: '', email: '',
  propertyAddress: '', community: '', floorPlan: '', timeline: 'Now', alsoBuying: 'No',
  reasonForMoving: '', howHeard: '', consentText: false, consentEmail: false,
  assignedAgentId: '', estimatedPrice: '', listingAppointmentAt: '', listingAppointmentLocation: '', result: '', followUpDate: '',
  notes: '', status: 'New', street: '', city: '', state: 'FL', zip: '',
  pipelineStatus: '', plannedListDate: '',
  pool: '', view: '', priceMin: '', priceMax: '',
};

// Who gets the alert/task: the assigned agent, else the first Admin, else the creator.
export const alertTarget = (interest, team, uid) =>
  interest.assignedAgentId || (team || []).find((t) => t.role === 'Admin')?.uid || uid;

const addTask = async (agent, data, uid) => {
  const r = push(ref(database, `users/${agent}/tasks`));
  await set(r, {
    role: 'Agent', appliesTo: 'All', mode: 'task', status: 'pending', assignee: agent,
    createdAt: nowIso(), updatedAt: nowIso(), createdBy: uid || null, completedAt: null, notes: '', ...data,
  });
  return r.key;
};

export const createSellerInterest = async (data, uid, team) => {
  const r = push(ref(database, SI_PATH));
  const target = alertTarget(data, team, uid);
  const due = new Date(Date.now() + 60 * 60 * 1000);
  const label = fullName(data) || 'New seller';
  const record = { ...EMPTY_INTEREST, ...data, name: fullName(data), offMarketProspect: data.result === OFF_MARKET_RESULT, status: data.status || 'New', leadSource: (data.howHeard === 'Other' ? data.howHeardOther : data.howHeard) || 'Seller interest', createdAt: nowIso(), updatedAt: nowIso(), createdBy: uid || null };
  // Private by default: owned by the assigned agent (an agent adding their own lead), else the creator.
  Object.assign(record, newOwnership(data.ownerUid || data.assignedAgentId || uid));
  // Lead generated by: agent when they enter their own lead, team when assigned by someone else or imported.
  record.leadGeneratedBy = LEAD_GENERATED_BY.includes(data.leadGeneratedBy) ? data.leadGeneratedBy : leadGeneratedByDefault({ creatorUid: uid, ownerUid: record.ownerUid, imported: !!data.imported });
  await update(ref(database), { [`${SI_PATH}/${r.key}`]: record, ...indexOps('sellerInterests', r.key, record) });
  let callTaskId = null;
  if (target) {
    callTaskId = await addTask(target, {
      name: `Call within the hour: ${label}`, title: `Call within the hour: ${label}`,
      description: `New seller interest${data.propertyAddress ? ` for ${data.propertyAddress}` : ''}. Timeline: ${data.timeline || 'n/a'}.${data.phone ? ` Phone: ${data.phone}.` : ''}`,
      type: 'Follow-up', source: 'seller-interest', sellerInterestId: r.key,
      dueDate: isoDay(due), dueTime: hhmm(due), dueAt: due.toISOString(),
    }, uid);
    try {
      await createNotification(target, {
        type: 'seller_interest', title: 'New seller interest',
        message: `${label}${data.propertyAddress ? ` - ${data.propertyAddress}` : ''}. Call within the hour.`,
        link: '/pipeline', sellerInterestId: r.key, channel: 'in-app',
      });
    } catch (e) { console.error('Could not create in-app alert', e); }
  }
  const contactId = await syncContact({ id: r.key, ...record }, uid);
  const extra = await scheduleSideEffects({ id: r.key, ...record, contactId }, {}, uid, team);
  await update(ref(database, `${SI_PATH}/${r.key}`), { alertTo: target || null, callTaskId, ...extra });
  await mirror({ id: r.key, ...record }, uid);
  return r.key;
};

// Listing appointment -> task + crm/appointments entry (no reminders queued, nothing sent).
// Follow back on date -> re-engage task in the agent's task list. Each fires once per new date.
const scheduleSideEffects = async (next, prev, uid, team) => {
  const out = {};
  const agent = alertTarget(next, team, uid);
  const who = fullName(next) || 'seller';
  if (agent && next.listingAppointmentAt && next.listingAppointmentAt !== prev.apptTaskFor) {
    const d = new Date(next.listingAppointmentAt);
    // Rescheduled: cancel the previous appointment so Google Calendar sync moves it instead of keeping a duplicate.
    if (prev.appointmentId) { try { await update(ref(database, `crm/appointments/${prev.appointmentId}`), { status: 'cancelled', cancelledAt: nowIso(), cancelledReason: 'rescheduled' }); } catch (e) { console.error('Could not cancel previous appointment', e); } }
    const a = push(ref(database, 'crm/appointments'));
    await set(a, { contactId: next.contactId || null, contactName: who, agentUid: agent, startAt: d.toISOString(), type: 'In person', location: next.listingAppointmentLocation || next.propertyAddress || '', notes: 'Listing appointment (seller interest)', status: 'scheduled', sellerInterestId: next.id, createdAt: nowIso(), createdBy: uid || null });
    out.apptTaskId = await addTask(agent, {
      name: `Listing appointment: ${who}`, title: `Listing appointment: ${who}`,
      description: `${next.propertyAddress || ''}${next.community ? ` (${next.community})` : ''}${next.phone ? `. Phone: ${next.phone}` : ''}`,
      type: 'Appointment', source: 'seller-interest-appointment', sellerInterestId: next.id, appointmentId: a.key,
      dueDate: isoDay(d), dueTime: hhmm(d), dueAt: d.toISOString(),
    }, uid);
    await recordOutreach(uid, 'appointment', 'seller-interest', { contactId: next.contactId || undefined, contactName: who, apptType: 'Listing appointment', apptAt: d.toISOString() });
    requestCalendarSync();
    out.appointmentId = a.key; out.apptTaskFor = next.listingAppointmentAt;
  }
  // Appointment date cleared: cancel the Resi appointment (Google Calendar sync then deletes its event) and its task.
  if (!next.listingAppointmentAt && prev.appointmentId) {
    try {
      await update(ref(database, `crm/appointments/${prev.appointmentId}`), { status: 'cancelled', cancelledAt: nowIso(), cancelledReason: 'cleared' });
      if (prev.apptTaskId && agent) await update(ref(database, `users/${agent}/tasks/${prev.apptTaskId}`), { status: 'cancelled', notes: 'Appointment date cleared', updatedAt: nowIso() });
      requestCalendarSync();
    } catch (e) { console.error('Could not cancel cleared appointment', e); }
    out.appointmentId = null; out.apptTaskId = null; out.apptTaskFor = null;
  }
  // Confirm-appointment task (day before, with a Text quick action). Created, moved or removed with the appointment.
  try {
    Object.assign(out, await syncConfirmTask({ kind: 'seller', id: next.id, firstName: next.firstName, name: who, phone: next.phone, address: next.listingAppointmentLocation || next.propertyAddress || '', apptAt: next.listingAppointmentAt, agent, agentName: (team || []).find((t) => t.uid === agent)?.name || '' }, prev, uid));
  } catch (e) { console.error('Could not sync confirm task', e); }
  if (agent && next.pipelineStatus === SI_WAITING && next.plannedListDate && next.plannedListDate !== prev.listCheckInFor) {
    out.listCheckInTaskId = await addTask(agent, {
      name: `Check in with ${who} before listing`, title: `Check in with ${who} before listing`,
      description: `Seller is waiting to list. Planned list date: ${next.plannedListDate}.${next.propertyAddress ? ` ${next.propertyAddress}.` : ''}${next.phone ? ` Phone: ${next.phone}.` : ''}`,
      type: 'Follow-up', source: 'seller-interest-list-checkin', sellerInterestId: next.id, dueDate: checkInDay(next.plannedListDate),
    }, uid);
    out.listCheckInFor = next.plannedListDate; out.listCheckInOwner = agent;
  }
  // Older records only: the follow-back date field is no longer in the form.
  if (agent && next.result === FOLLOW_BACK_RESULT && next.followUpDate && next.followUpDate !== prev.followUpTaskDate) {
    out.followUpTaskId = await addTask(agent, {
      name: `Re-engage ${who}`, title: `Re-engage ${who}`,
      description: `Seller interest: follow back on ${next.followUpDate}.${next.propertyAddress ? ` ${next.propertyAddress}.` : ''}${next.phone ? ` Phone: ${next.phone}.` : ''}`,
      type: 'Follow-up', source: 'seller-interest-followup', sellerInterestId: next.id, dueDate: next.followUpDate,
    }, uid);
    out.followUpTaskDate = next.followUpDate; out.followUpTaskOwner = agent;
  }
  return out;
};

// Saves edits, runs the appointment / follow-back side effects, and keeps the CRM contact in sync.
export const updateSellerInterest = async (prev, patch, uid, team) => {
  const next = { ...prev, ...patch };
  const extra = await scheduleSideEffects(next, prev, uid, team);
  const out = { ...patch, ...extra, name: fullName(next), offMarketProspect: next.result === OFF_MARKET_RESULT, updatedAt: nowIso() };
  delete out.id;
  delete out.ownerUid; delete out.sharedWith; // ownership changes only via Transfer / Share
  // An admin changing the assigned agent is a transfer: the new agent owns it, the previous owner leaves.
  if (patch.assignedAgentId && patch.assignedAgentId !== ownerOf(prev)) {
    const sharedWith = { ...(prev.sharedWith || {}) }; delete sharedWith[patch.assignedAgentId];
    out.ownerUid = patch.assignedAgentId; out.sharedWith = Object.keys(sharedWith).length ? sharedWith : null;
    const ops = Object.fromEntries(Object.entries(out).map(([k, v]) => [`${SI_PATH}/${prev.id}/${k}`, v === undefined ? null : v]));
    await update(ref(database), { ...ops, ...indexOps('sellerInterests', prev.id, { ownerUid: out.ownerUid, sharedWith }, prev) });
  } else await update(ref(database, `${SI_PATH}/${prev.id}`), out);
  await syncContact({ ...next, ...out, id: prev.id }, uid);
  await mirror({ ...next, ...out, id: prev.id }, uid);
  return out;
};

// Creates or updates the matching CRM contact (Leads). Matches by contactId, then email, then phone,
// so there is never a duplicate or double entry. Links both ways:
//   crm/sellerInterests/{id}/contactId  and  crm/contacts/{cid}/sellerInterestIds/{id} = true
const digits = (s) => String(s || '').replace(/\D/g, '').slice(-10);
export const syncContact = (interest, uid) => syncPipelineContactApp('seller', SI_PATH, interest, uid);
export const ensureContactForInterest = (interest, uid) => syncContact(interest, uid);

// The CMA builder calls this when a CMA is made for an interest (opened from "Create CMA").
export const linkCmaToInterest = async (interestId, cma) => {
  if (!interestId) return;
  await update(ref(database, `${SI_PATH}/${interestId}`), { cma: { ...cma, at: nowIso() }, updatedAt: nowIso() });
  // Each finished CMA is also kept in the seller's list (crm/sellerInterests/{id}/cmas/{key}).
  if (cma?.status === 'created') await push(ref(database, `${SI_PATH}/${interestId}/cmas`), { title: `CMA - ${cma.address || 'Subject property'}`, fileName: cma.fileName || '', recommendedList: cma.recommendedList || '', at: nowIso() });
};

// Saved from the Seller Net Sheet when opened from a seller (crm/sellerInterests/{id}/netSheets/{key}).
export const addNetSheetToInterest = async (interestId, sheet) => {
  if (!interestId) return null;
  const r = await push(ref(database, `${SI_PATH}/${interestId}/netSheets`), { ...sheet, at: nowIso() });
  await update(ref(database, `${SI_PATH}/${interestId}`), { updatedAt: nowIso() });
  return r.key;
};

export const ownsInterest = (si, uid, role) => !!si && (role === 'Admin' || (si.ownerUid ? si.ownerUid === uid : (si.assignedAgentId === uid || si.createdBy === uid)) || !!(si.sharedWith && si.sharedWith[uid]));

// Starting sale price for tools: middle of the price range, else the estimated price.
export const interestPrice = (si) => {
  const n = (x) => Number(String(x || '').replace(/[^0-9.]/g, '')) || 0;
  const lo = n(si?.priceMin), hi = n(si?.priceMax);
  if (lo && hi) return Math.round((lo + hi) / 2);
  return lo || hi || n(si?.estimatedPrice) || '';
};

// Called by the Seller form after the transaction is created from an interest.
export const markInterestConverted = async (interestId, transactionId, ownerUid) => {
  if (!interestId || !transactionId) return;
  await update(ref(database, `${SI_PATH}/${interestId}`), {
    status: 'Converted', convertedTransactionId: transactionId, convertedOwnerUid: ownerUid || null, convertedAt: nowIso(), updatedAt: nowIso(),
  });
  // One contact per person: the new transaction links to the same contact as the pipeline record.
  try {
    const cid = (await get(ref(database, `${SI_PATH}/${interestId}/contactId`))).val();
    if (cid) await update(ref(database), { [`transactions/${transactionId}/contactId`]: cid, [`crm/contacts/${cid}/transactionIds/${transactionId}`]: true });
  } catch (e) { console.error('Could not link the transaction to the contact', e); }
  try { await removePocketListing(interestId); } catch (e) { console.error('Could not remove pocket listing', e); }
};

export const getSellerInterest = async (id) => {
  const s = await get(ref(database, `${SI_PATH}/${id}`));
  return s.val() ? { id, ...s.val() } : null;
};
